/* ============================================================
   Chart palette.

   One place for every colour a chart uses, so the app stops
   holding three unrelated palettes: a Material ramp cycled per
   series in transformChartData, a magenta/violet/blue trio for
   HML, and an orange/blue pair for recovery.

   These values are not taste. They were run through the
   data-viz validator - six checks: lightness band, chroma
   floor, colour-vision-deficiency separation of adjacent pairs,
   normal-vision separation, and contrast against the surface:

     validate_palette.js "<the CATEGORICAL list>" \
       --mode light --surface "#ffffff"
     -> ALL CHECKS PASS (worst adjacent CVD dE 9.1, normal 19.6)

     validate_palette.js "#86b6ef,#2a78d6,#184f95" \
       --mode light --surface "#ffffff" --ordinal
     -> ALL CHECKS PASS (monotone L, single hue, light end 2.11:1)

   Re-run both if you touch a value. Three categorical slots sit
   below 3:1 on white, which the validator allows only with
   relief: every chart here keeps a labelled legend, so identity
   never rests on colour alone.
   ============================================================ */

/* Categorical: identity, no order. Assigned in this fixed order and never
   cycled - the hue belongs to the series, not to its rank. */
export const CHART_CATEGORICAL = [
  "#2a78d6", // blue
  "#eb6834", // orange
  "#1baf7a", // aqua
  "#eda100", // yellow
  "#e87ba4", // magenta
  "#008300", // green
  "#4a3aa7", // violet
  "#e34948", // red
];

/* Past the eight slots a chart has more series than a reader can tell apart.
   Rather than inventing hues, the tail goes grey: it still plots, and it reads
   as the long tail it is. Fold it into a real "Other" series when the product
   can afford to. */
const CHART_TAIL = ["#9aa1b4", "#b4bac9", "#c8ccd8"];

export const chartSeriesColors = (count = 0) =>
  Array.from({ length: count }, (_, index) =>
    index < CHART_CATEGORICAL.length
      ? CHART_CATEGORICAL[index]
      : CHART_TAIL[(index - CHART_CATEGORICAL.length) % CHART_TAIL.length],
  );

/* Ordinal: discrete but ordered (Low -> Medium -> High). One hue, light to
   dark, so the ranking is visible without reading the legend. Three unrelated
   hues cannot show an order. */
export const CHART_ORDINAL_BLUE = ["#86b6ef", "#2a78d6", "#184f95"];

/* Status: reserved for state, never for "series 4". Ships with a label. */
export const CHART_STATUS = {
  good: "#0ca30c",
  warning: "#fab219",
  serious: "#ec835a",
  critical: "#d03b3b",
};

/* Chart furniture, kept recessive: the data is the ink. */
export const CHART_GRID = "#f1f3fa"; // --dsh-border-soft
export const CHART_TICK = "#838a9e"; // --dsh-muted
export const CHART_SURFACE = "#ffffff"; // the panel the marks sit on
export const CHART_CURSOR = "rgba(255, 104, 67, 0.06)"; // brand, barely there
